import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import api from "../../api/axios";
import "./Navigationnew.css";

function Navigationnew({
  selectedAgent,
  setSelectedAgent,
  remainingActiveAgents,
  currentConversationId,
  artifactsOpen,
setArtifactsOpen = () => {},
  activeAgents = [],
  setActiveAgents,
  setCurrentAgent,
  currentAgent,
  allWorkspaceAgents = [],
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { agentId } = useParams();
  const pathname = location.pathname.toLowerCase();

  const isChatPage =
    pathname.includes("/new-home") ||
    pathname.includes("/chatinterface");

const pageTitleMap = {
  "/home": "Home",
  "/dashboard": "Dashboard",
  "/analytics": "Analytics",
  "/knowledgebase": "Knowledge Base",
  "/logs": "Logs",
  "/settings": "Settings",
  "/kb": "KB",
  // ✅ FIX: Dynamically check if we are actually editing an existing agent, otherwise say "Create Agent"
  "/screeningagent": location.state?.existingAgent ? "Update Agent" : "Create Agent",
  "/create-agent": "Create Agent " // Safety catch for manual creation
};
  const currentPageTitle = Object.keys(pageTitleMap).find((key) =>
    pathname.includes(key)
  );
  const pageTitle = pageTitleMap[currentPageTitle] || "Dashboard";

  // ── Local agent state (for display fallback only — does NOT conflict with prop) ──
  const [localAgent, setLocalAgent] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);

  // ── Dropdown states ──
  const [showAttachDropdown, setShowAttachDropdown] = useState(false);
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  // ── Artifact states ──
  const [conversationArtifacts, setConversationArtifacts] = useState([]);
  const [allArtifacts, setAllArtifacts] = useState([]);
  const [previewArtifact, setPreviewArtifact] = useState(null);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);

  // ── Refs ──
  const agentDropdownRef = useRef(null);
  const attachDropdownRef = useRef(null);
  const sidebarRef = useRef(null);

  // =========================================================
  // FETCH LOCAL AGENT — for display fallback only
  // Uses setLocalAgent, NOT setCurrentAgent (prop)
  // =========================================================
  useEffect(() => {
    const fetchAgent = async () => {
      if (selectedAgent) {
        setLocalAgent(selectedAgent);
        setIsNewUser(false);
        return;
      }

      try {
        if (agentId) {
          const res = await api.get(`/agents/${agentId}`);
          const agentData = Array.isArray(res.data) ? res.data[0] : res.data;
          setLocalAgent(agentData || null);
          setIsNewUser(false);
          return;
        }

        const agentsRes = await api.get("/agents");
        const agents = agentsRes.data || [];

        if (!agents.length) {
          setLocalAgent(null);
          setIsNewUser(true);
          return;
        }

        let lastConv = null;
        let lastAgent = null;

        for (const agent of agents) {
          try {
            const convRes = await api.get(
              `/agents/${agent.id}/conversations?limit=1`
            );
            const convs =
              convRes.data?.conversations ||
              (Array.isArray(convRes.data) ? convRes.data : []);

            if (convs.length > 0) {
              const newest = convs[0];
              if (
                !lastConv ||
                new Date(newest.created_at) > new Date(lastConv.created_at)
              ) {
                lastConv = newest;
                lastAgent = agent;
              }
            }
          } catch {
            // skip individual agent errors
          }
        }

        if (lastAgent) {
          setLocalAgent(lastAgent);
          setIsNewUser(false);
        } else {
          setLocalAgent(agents[0]);
          setIsNewUser(false);
        }
      } catch (err) {
        console.log("Navigationnew fetchAgent error:", err);
        setLocalAgent(null);
        setIsNewUser(true);
      }
    };

    fetchAgent();
  }, [agentId, selectedAgent]);

  // =========================================================
  // OUTSIDE CLICK HANDLERS
  // =========================================================
 useEffect(() => {
  const handleClick = (e) => {

    // If preview modal is open,
    // don't close sidebar on outside click
    if (previewArtifact) return;

    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(e.target)
    ) {
      setArtifactsOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClick);

  return () =>
    document.removeEventListener("mousedown", handleClick);

}, [previewArtifact]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(e.target)
      ) {
        setShowAgentDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        attachDropdownRef.current &&
        !attachDropdownRef.current.contains(e.target)
      ) {
        setShowAttachDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // =========================================================
  // FETCH ARTIFACTS
  // =========================================================
  const handleArtifacts = async () => {
    setArtifactsOpen(true);

    try {
      setLoadingArtifacts(true);

      const targetId = agentId || currentAgent?.id || localAgent?.id;

      if (!targetId) {
        setAllArtifacts([]);
        setConversationArtifacts([]);
        return;
      }

      const allRes = await api.get(`/artifacts/agent/${targetId}`);
      const allData = Array.isArray(allRes.data) ? allRes.data : [];
      setAllArtifacts(allData);

      if (currentConversationId) {
        const currentConvRes = await api.get(
          `/artifacts/conversation/${currentConversationId}`
        );
        const convData = Array.isArray(currentConvRes.data)
          ? currentConvRes.data
          : [];
        setConversationArtifacts(convData);
      } else {
        setConversationArtifacts([]);
      }
    } catch (err) {
      console.log(err);
      setAllArtifacts([]);
      setConversationArtifacts([]);
    } finally {
      setLoadingArtifacts(false);
    }
  };

  // =========================================================
  // ATTACHABLE AGENTS — ALL active agents shown, tick for attached
  // Currently opened agent excluded (can't attach to itself)
  // =========================================================
  const attachableAgents = allWorkspaceAgents.filter((agent) => {
    const isActive =
      agent.status === "Active" || parseInt(agent.doc_count || 0) > 0;
    const isCurrentAgent =
      String(agent.id) === String(agentId) ||
      String(agent.id) === String(currentAgent);
    return isActive && !isCurrentAgent; // ← show all active except the current one
  });

  // ── Derived display values ──
  // localAgent has the full object (.name etc), currentAgent is just an ID string
  // So always prioritize localAgent for display
  const displayAgent = localAgent || currentAgent;
  const agentName =
    isNewUser || !displayAgent
      ? null
      : activeAgents.length > 1
      ? activeAgents.map((a) => a.name).filter(Boolean).join(" + ")
      : displayAgent.name || "Agent";
  const agentModel = displayAgent?.model || "ChatGPT 4.0";

  return (
    <>
      <div className="navigationnew-wrapper">

        {/* ── LEFT: Agent name + model pill ── */}
        <div className="navigationnew-left">

          {!isChatPage ? (

            <div className="navigationnew-page-title-wrap">
              <button className="navigationnew-menu-btn">☰</button>
              <h2 className="navigationnew-page-title">{pageTitle}</h2>
            </div>

          ) : (

            <>
              {isNewUser || !displayAgent ? (

                <span className="navigationnew-brand-text">KINEXAR</span>

              ) : (

                <div
                  className="navigationnew-agent-block"
                  ref={agentDropdownRef}
                >

                  <h2 className="navigationnew-agent-name">{agentName}</h2>

                  <div className="navigationnew-llm-pill">
                    <span className="green-dot"></span>
                    {agentModel}
                  </div>

                  {/* ── ATTACH AGENT CAPSULE (before Switch Agents) ── */}
                  {attachableAgents.length > 0 && setActiveAgents && (
                    <div
                      className="navigationnew-agent-dropdown-wrapper"
                      ref={attachDropdownRef}
                    >
                      <button
                        className="navigationnew-switch-btn navigationnew-attach-btn"
                        onClick={() =>
                          setShowAttachDropdown((prev) => !prev)
                        }
                      >
                        <img src="/images/Xlogo.svg" alt="" />
                        <span>Attach Agent</span>
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {showAttachDropdown && (
                        <div className="navigationnew-agent-dropdown">
                          {attachableAgents.map((agent) => {
                            const isAttached = activeAgents.some(
                              (a) => String(a.id) === String(agent.id)
                            );
                            return (
                              <div
                                key={agent.id}
                                className={`navigationnew-agent-option ${
                                  isAttached ? "attached" : ""
                                }`}
                                onClick={() => {
                                  if (isAttached) {
                                    if (activeAgents.length > 1) {
                                      const updated = activeAgents.filter(
                                        (a) =>
                                          String(a.id) !== String(agent.id)
                                      );
                                      setActiveAgents(updated);
                                      if (
                                        setCurrentAgent &&
                                        String(currentAgent?.id || currentAgent) ===
                                          String(agent.id)
                                      ) {
                                        setCurrentAgent(updated[0].id);
                                      }
                                    }
                                  } else {
                                    setActiveAgents((prev) => [
                                      ...prev,
                                      agent,
                                    ]);
                                  }
                                  setShowAttachDropdown(false);
                                }}
                              >
                                <img src="/images/Xlogo.svg" alt="" />
                                <div>
                                  <h4>{agent.name}</h4>
                                  <p>
                                    {agent.description || "AI Assistant"}
                                  </p>
                                </div>
                                {isAttached && (
                                  <span
                                    style={{
                                      marginLeft: "auto",
                                      fontSize: "10px",
                                      color: "#10B981",
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  >
                                    ✓ Attached
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── SWITCH AGENTS CAPSULE ── */}
                  {remainingActiveAgents?.length > 0 && (
                    <div className="navigationnew-agent-dropdown-wrapper">
                      <button
                        className="navigationnew-switch-btn"
                        onClick={() =>
                          setShowAgentDropdown((prev) => !prev)
                        }
                      >
                        <img src="/images/Xlogo.svg" alt="" />
                        <span>Switch Agents</span>
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {showAgentDropdown && (
                        <div className="navigationnew-agent-dropdown">
                          {remainingActiveAgents.map((agent) => (
                            <div
                              key={agent.id}
                              className="navigationnew-agent-option"
                              onClick={() => {
                                setShowAgentDropdown(false);
                                navigate(`/chatinterface/${agent.id}`);
                              }}
                            >
                              <img src="/images/Xlogo.svg" alt="" />
                              <div>
                                <h4>{agent.name}</h4>
                                <p>
                                  {agent.description || "AI Assistant"}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}
            </>
          )}
        </div>

        {/* ── RIGHT: Icon buttons ── */}
        <div className="navigationnew-right">

          <button
            className={`navigationnew-icon-btn ${
              location.pathname === "/analytics" ? "active" : ""
            }`}
            onClick={() => navigate("/analytics")}
            title="Analytics"
          >
            <img src="/images/navilycs.svg" alt="analytics" />
          </button>

          <button
            className={`navigationnew-icon-btn ${
              location.pathname === "/logs" ? "active" : ""
            }`}
            onClick={() => navigate("/logs")}
            title="Logs"
          >
            <img src="/images/navilogs.svg" alt="logs" />
          </button>

          <button
            className={`navigationnew-icon-btn ${
              location.pathname === "/settings" ? "active" : ""
            }`}
            onClick={() => navigate("/settings")}
            title="Settings"
          >
            <img src="/images/navisett.svg" alt="settings" />
          </button>

        
{isChatPage && (
  <>
    <div className="navigationnew-divider"></div>

    <button
      className="navigationnew-artifact-btn"
      onClick={handleArtifacts}
      title="Artifacts"
    >
      <img src="/images/navifacts.svg" alt="artifacts" />
      <span>Artifacts</span>
    </button>
  </>
)}

        </div>
      </div>

      {/* ===================================================== */}
      {/* ARTIFACT SIDEBAR                                       */}
      {/* ===================================================== */}
      <div
        className={`artifact-sidebar ${artifactsOpen ? "open" : ""}`}
        ref={sidebarRef}
      >
        <div className="artifact-sidebar-header">
          <div className="artifact-sidebar-title">
            <img src="/images/navifacts.svg" alt="artifact" />
            <span>Artifacts</span>
          </div>
          <button
            className="artifact-close-btn"
            onClick={() => setArtifactsOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="artifact-sidebar-body">
          <div className="artifact-current-agent">
            {displayAgent?.name || "No Agent Selected"}
          </div>

          {loadingArtifacts && (
            <div className="artifact-empty">
              <p>Loading artifacts...</p>
            </div>
          )}

          {!loadingArtifacts &&
            conversationArtifacts.length === 0 &&
            allArtifacts.length === 0 && (
              <div className="artifact-empty">
                <img src="/images/navifacts.svg" alt="empty" />
                <h4>No artifacts generated</h4>
                <p>
                  This agent has not generated any documents or images yet.
                </p>
              </div>
            )}

          {!loadingArtifacts && (
            <>
              {conversationArtifacts.length > 0 && (
                <>
                  <div className="artifact-section-title">
                    FROM THIS CONVERSATION
                  </div>
                 {conversationArtifacts.map((item, index) => (
  <div
    className="artifact-item"
    key={index}
    onClick={() => setPreviewArtifact(item)}
  >
                      <div className="artifact-item-icon">
                        <img src="/images/livearti.svg" alt="artifact" />
                      </div>
                      <div className="artifact-item-content">
                        <h5>{item.name || item.file_name || "Untitled"}</h5>
                        <p>{item.type || "document"}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {conversationArtifacts.length > 0 &&
                allArtifacts.length > 0 && (
                  <div className="artifact-divider"></div>
                )}

              {allArtifacts.length > 0 && (
                <>
                  <div className="artifact-section-title all-artifacts-title">
                    ALL ARTIFACTS
                  </div>
                 {allArtifacts.map((item, index) => (
  <div
    className="artifact-item artifact-item-secondary"
    key={index}
    onClick={() => setPreviewArtifact(item)}
  >
                      <div className="artifact-item-icon">
                        <img src="/images/allarti2.svg" alt="artifact" />
                      </div>
                      <div className="artifact-item-content">
                        <h5>
                          {item.name || item.file_name || "Untitled"}
                        </h5>
                        <p>{item.type || "document"}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
      {/* =========================================
    ARTIFACT PREVIEW MODAL
========================================= */}

{previewArtifact && (
  <div
    className="artifact-preview-overlay"
    onClick={() => setPreviewArtifact(null)}
  >
    <div
      className="artifact-preview-modal"
      onClick={(e) => e.stopPropagation()}
    >

      {/* HEADER */}
      <div className="artifact-preview-header">

        <h4>
          {previewArtifact.file_name ||
            previewArtifact.name ||
            "Preview"}
        </h4>

        <button
          className="artifact-preview-close"
          onClick={() => setPreviewArtifact(null)}
        >
          ×
        </button>
      </div>

      {/* CONTENT */}
      <div className="artifact-preview-body">

        {previewArtifact.type === "image" ? (

          <img
            src={previewArtifact.file_url}
            alt="preview"
            className="artifact-preview-image"
          />

        ) : (

          <iframe
            src={previewArtifact.file_url}
            title="Document Preview"
            className="artifact-preview-document"
          />

        )}

      </div>
    </div>
  </div>
)}
    </>
  );
}

export default Navigationnew;